import { LoadStatus, type PortalStore, portalStoreToken } from '@servicetitan/cp-ui';
import { inject, injectable } from '@servicetitan/react-ioc';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { SpecialSettingsStore } from '../../../../../../common/stores/special-settings.store';
import { DashboardApi } from '../../../../../api';
import { JobAppointmentItem, JobAppointmentStatusWeb } from '../../../../../api/portal-jobs.api';

@injectable()
export class JobAppointmentsListDataStore {
    @observable loadStatus = LoadStatus.None;
    @observable appointments?: JobAppointmentItem[];
    @observable totalCount = 0;
    @observable page = 1;
    @observable hasMore = false;
    @observable appointmentsFilter?: JobAppointmentStatusWeb[];

    constructor(
        @inject(portalStoreToken) private portalStore: PortalStore,
        @inject(DashboardApi) private dashboardApi: DashboardApi,
        @inject(SpecialSettingsStore) private specialSettings: SpecialSettingsStore
    ) {
        makeObservable(this);
    }

    @action
    loadJobAppointments = async (jobId: number, page: number, pageSize = 20) => {
        this.loadStatus = LoadStatus.Loading;

        try {
            const statuses = await this.loadAppointmentsSettingsFilters();
            const result = await this.dashboardApi.jobs.getJobAppointments(
                jobId,
                statuses,
                page,
                pageSize
            );
            runInAction(() => {
                if (page === 1) {
                    this.appointments = result.data.data ?? [];
                } else {
                    this.appointments = this.appointments?.concat(result.data.data ?? []) ?? [];
                }
                this.totalCount = result.data.totalCount ?? 0;
                this.hasMore = result.data.hasMore;
                this.page = result.data.page ?? 1;
                this.loadStatus = LoadStatus.Ok;
            });
        } catch {
            this.showErrorMessage();
            runInAction(() => {
                this.loadStatus = LoadStatus.Error;
            });
        }
    };

    async loadAppointmentsSettingsFilters(): Promise<JobAppointmentStatusWeb[]> {
        if (this.appointmentsFilter) {
            return this.appointmentsFilter;
        }

        const defaultFilter = {
            scheduled: true,
            dispatched: true,
            working: true,
            hold: true,
            done: true,
            canceled: true,
        };
        const filter = {
            ...defaultFilter,
            ...((await this.specialSettings.getAsync<Record<string, boolean>>(
                'appointment-filter'
            )) ?? {}),
        };
        const result: JobAppointmentStatusWeb[] = [];
        if (filter?.scheduled) {
            result.push(JobAppointmentStatusWeb.Scheduled);
        }
        if (filter?.dispatched) {
            result.push(JobAppointmentStatusWeb.Dispatched);
        }
        if (filter?.working) {
            result.push(JobAppointmentStatusWeb.Working);
        }
        if (filter?.hold) {
            result.push(JobAppointmentStatusWeb.Hold);
        }
        if (filter?.done) {
            result.push(JobAppointmentStatusWeb.Done);
        }
        if (filter?.canceled) {
            result.push(JobAppointmentStatusWeb.Canceled);
        }

        this.appointmentsFilter = result;
        return result;
    }

    showErrorMessage = () => {
        this.portalStore.event('notify', {
            title: 'An error occurred',
            description: 'Please, try again later.',
            status: 'critical',
        });
    };
}
